/**
 * @fileoverview Vendor Home Page
 * 
 * Sprint 27: Vendor App UX Completion
 * 
 * Displays vendor context (name, status, market) and quick stats.
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/store/auth';
import { useVendorStore } from '@/store/vendor';
import { vendorApi } from '@/lib/api';
import { logger } from '@/lib/logger';
import { 
  Store, 
  MapPin, 
  Package, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  TrendingUp 
} from 'lucide-react';

export default function HomePage() {
  const { vendor } = useAuthStore();
  const { stats, setStats, isLoading } = useVendorStore();

  useEffect(() => {
    // Fetch stats on mount
    vendorApi
      .getStats()
      .then((data) => {
        setStats(data);
      })
      .catch((error) => {
        logger.error('Failed to load vendor stats', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Status badge colors
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'suspended':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome back!</h1>
          <p className="text-muted-foreground">
            Here's an overview of your store and today's activity.
          </p>
        </div>

        {/* Vendor Context Card */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            {/* Vendor Icon */}
            <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Store className="w-8 h-8 text-primary" />
            </div>

            {/* Vendor Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-foreground truncate">
                  {vendor?.name || 'Your Store'}
                </h2>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                    vendor?.status || 'inactive'
                  )}`}
                >
                  {(vendor?.status || 'inactive').toUpperCase()}
                </span>
              </div>

              {/* Market Info */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">
                  {vendor?.market_name || 'No market assigned'}
                </span>
                {vendor?.market_status !== undefined && (
                  <span
                    className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                      vendor.market_status
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {vendor.market_status ? 'Market Open' : 'Market Closed'}
                  </span>
                )}
              </div>

              {/* Market Type */}
              {vendor?.market_type && (
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  {vendor.market_type} Market
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Orders Today */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Orders Today</p>
                <p className="text-2xl font-bold text-foreground">
                  {isLoading ? '...' : (stats?.totalOrders || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Pending Orders */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-foreground">
                  {isLoading ? '...' : (stats?.activeOrders || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Completed Today */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-foreground">
                  {isLoading ? '...' : (stats?.completedToday || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Performance
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Avg Prep Time</p>
              <p className="text-lg font-semibold text-foreground">
                {stats?.avgPrepTime ? `${stats.avgPrepTime} min` : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rating</p>
              <p className="text-lg font-semibold text-foreground">
                {stats?.rating ? `${stats.rating.toFixed(1)} / 5` : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cancel Rate</p>
              <p className="text-lg font-semibold text-foreground">
                {stats?.cancelRate !== undefined ? `${stats.cancelRate.toFixed(1)}%` : '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Orders</p>
              <p className="text-lg font-semibold text-foreground">
                {stats?.totalOrders || '--'}
              </p>
            </div>
          </div>
        </div>

        {/* Market Inactive Warning */}
        {vendor?.market_status === false && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Market is currently closed</p>
              <p className="text-sm text-amber-700 mt-1">
                You won't receive new orders while your market is inactive. Orders will resume when the market reopens.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
